// Calibration & usage instructions for the user-facing app.
//
// This is the place to add any procedural guidance the user needs:
// how to bootstrap and calibrate per-vehicle features, how to record
// useful clips, what to do when a detector misbehaves. Add new
// sections here as we ship more procedures — the modal grows over
// time without adding more buttons in the chrome.
//
// Duplicated to admin/src/CalInstructionsModal.tsx — keep in sync.
// Only the import paths differ (none here actually — pure presentation).

import { ReactNode, useState } from 'react';

interface Props {
  onClose: () => void;
}

export function CalInstructionsModal({ onClose }: Props) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: 760, width: '95%', maxHeight: '92vh', overflow: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>Calibration & usage instructions</h3>
          <button className="modal-close" onClick={onClose} aria-label="close">×</button>
        </div>

        <div className="muted" style={{ fontSize: '0.85rem', marginBottom: 8 }}>
          Procedures for getting the most out of the app. Most thresholds
          tune themselves over time as more recordings come in — but the
          procedures below get you to a usable state faster, and validate
          that detectors are working as expected on your specific vehicle
          + mic setup.
        </div>

        <Section title="Cylinder identification (per-vehicle, one-time)" defaultOpen>
          <p>
            The app identifies each engine cylinder as a "persistent ID"
            (P-A, P-B, …) that stays the same across all of this vehicle's
            recordings. To get those IDs to display as actual engine
            cylinder numbers (Cyl 1, Cyl 4, …), you tell it which is which.
          </p>
          <ol>
            <li>
              Record at least <strong>3 clips</strong> of the engine running
              normally so the app's per-vehicle template stabilizes. Each
              clip 5–15 seconds is fine.
            </li>
            <li>
              Open <em>⚙ Configure → Open calibration…</em>. You'll see one
              row per persistent ID with a small spectrum thumbnail, the
              energy %, and the phase offset from your most recent clip.
            </li>
            <li>
              For each row, pick the physical cylinder it corresponds to
              from the dropdown.
              <br />
              <strong>How do you know which is which?</strong> The most
              reliable way is to do something controlled in the engine bay:
              <ul>
                <li>Pull a spark plug wire / coil pack on one cylinder, record a clip, observe which P-X has the lowest energy in this clip — that's the one.</li>
                <li>Or pull a fuel injector connector on one cylinder.</li>
                <li>Reconnect, repeat for the next cylinder.</li>
              </ul>
              You don't have to map them all at once. Partial maps are fine
              — the unassigned ones will keep showing as P-X until you fill
              them in.
            </li>
            <li>
              Once saved, the diagnostic modal labels each card with the
              physical cylinder number you assigned, with the persistent
              ID kept as a small sub-label for reference.
            </li>
          </ol>
          <p className="muted" style={{ fontSize: '0.85rem' }}>
            If the rows are blank or the dropdown is greyed out, the
            alignment hasn't bootstrapped yet — record one more clip and
            try again.
          </p>
        </Section>

        <Section title="Hybrid EV vs Engine mode (per-hybrid, calibration)">
          <p>
            For hybrid vehicles, the app tries to classify each clip as
            EV mode, engine mode, or off. The thresholds it ships with are
            global defaults; calibrating to your specific vehicle (one-time)
            sharpens the classification considerably.
          </p>
          <p>
            <strong>Five clips, one screenshot of the live monitor each:</strong>
          </p>
          <ol>
            <li>Pure EV mode at standstill (driveway, ready light on, foot off accelerator). ~10s.</li>
            <li>Pure EV mode while driving — coast or low-load at ~30 km/h. ~10s.</li>
            <li>Engine mode at idle — engine running, vehicle stationary. ~10s.</li>
            <li>Engine mode driving normally (~50 km/h cruise). ~10s.</li>
            <li>A transition: start in EV, then accelerate hard enough to force the engine on (or wait for warm-up cycle). ~30s.</li>
          </ol>
          <p>
            For each clip, also screenshot the <em>Live engine monitor</em>
            panel header so the badge state, live RPM number + confidence,
            and intensity reading are all visible. Send the clips +
            screenshots in for tuning. Until calibrated the badge will
            still flip, but may take 1–2 seconds to settle and may
            occasionally misclassify — both behaviors get tighter once
            tuned.
          </p>
        </Section>

        <Section title="Live RPM detector validation (any ICE vehicle)">
          <p>
            The big RPM number in the live monitor and the sparkline of
            recent values come from a fast browser-side detector. It'll
            occasionally disagree with the after-the-fact server-side
            consensus on hard cases — that's expected. To verify it's
            working on your vehicle:
          </p>
          <ol>
            <li>With engine off / cabin quiet, watch the RPM number for
              ~10s. It should read "—" (no lock). If it flickers a
              number, the detector is locking on noise — flag this for
              tuning.</li>
            <li>With engine at steady idle (~600–900 RPM), watch for
              10s. Number should be locked, confidence ≥ 50%.</li>
            <li>Rev to ~3000 RPM and back. The trace should track the
              rev within ~0.5s.</li>
            <li>Hard accel to 4000+ RPM under load. Trace should keep
              up.</li>
          </ol>
          <p>
            Capture a 30-second screen recording of step 3 if anything
            looks off — the trace shape tells us what to tune.
          </p>
        </Section>

        <Section title="Mic placement comparison (one-time)">
          <p>
            Mic placement matters more than you might expect — the same
            engine reads ~20 dB louder on the hood than in the cabin. That
            shifts every absolute-intensity threshold (EV-mode floor,
            leak score baselines, accessory SNR thresholds).
          </p>
          <ol>
            <li>Pick one vehicle. Set the engine to a steady idle.</li>
            <li>Record 10 seconds with the phone <strong>on the dashboard</strong>.</li>
            <li>Record 10 seconds with the phone <strong>in the cup-holder</strong>.</li>
            <li>Record 10 seconds with the phone <strong>just outside the open hood</strong>.</li>
          </ol>
          <p>
            Use <em>Configure → Microphone location</em> to label each clip
            with where the phone actually was. The intensity readings in
            the diagnostic modal will show how much each placement adds
            or subtracts — that's the offset that future per-mic
            calibrations apply.
          </p>
        </Section>

        <Section title="Why the app asks you questions">
          <p>
            After most recordings you may see the <em>Feedback</em> panel
            with a small question — "what did your gauge read?", "was the
            engine running?", "did you have work done recently?". These
            are <strong>uncertainty-gated</strong> — the app only asks
            when answering would actually teach it something.
          </p>
          <p>
            Three rules to keep the data clean:
          </p>
          <ol>
            <li>
              <strong>Answer when you're sure.</strong> One-tap chips for
              the common case.
            </li>
            <li>
              <strong>Press <em>Ignore</em> when you're not sure.</strong> A
              wrong answer pollutes the calibration set worse than no
              answer at all. The Ignore button is explicitly there so you
              never have to guess.
            </li>
            <li>
              <strong>Skip the panel entirely if you're busy.</strong>
              Pending questions expire after 48 h and won't pile up — the
              app caps you at 3 pending across the whole account.
            </li>
          </ol>
          <p className="muted" style={{ fontSize: '0.85rem' }}>
            The footer shows your running total of ground-truth answers
            per vehicle. As that grows, per-vehicle thresholds replace
            the global defaults and detection accuracy improves.
          </p>
        </Section>

        <Section title="General recording tips">
          <ul>
            <li>
              <strong>Clip length 5–15 seconds.</strong> Shorter loses
              cycle context; longer is wasteful and slower to analyze.
            </li>
            <li>
              <strong>Mic placement should be consistent within a
              vehicle.</strong> Switching from cabin to hood between
              recordings makes the longitudinal trends jump even when
              nothing about the engine has changed.
            </li>
            <li>
              <strong>Steady RPM is easier to interpret than transient.</strong>
              For diagnostics, a 10-second clip at a steady idle reveals
              more than a 10-second clip mid-acceleration.
            </li>
            <li>
              <strong>Set the gauge RPM label when you can.</strong> Even
              one or two per vehicle dramatically tightens the
              harmonic-correction multipliers.
            </li>
            <li>
              <strong>Mark a baseline.</strong> Once you have a recording
              you trust represents "vehicle in good shape", set it as the
              vehicle's baseline (Recent clips → Set as baseline). All
              future drift comparisons run against it.
            </li>
          </ul>
        </Section>
      </div>
    </div>
  );
}

// ---- Collapsible section helper -------------------------------------

function Section({
  title, defaultOpen, children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div style={{
      marginTop: 8,
      borderTop: '1px solid var(--border)',
      paddingTop: 8,
    }}>
      <button
        className="secondary"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%', textAlign: 'left',
          padding: '0.4rem 0.6rem', fontWeight: 700,
        }}
      >
        {open ? '▾' : '▸'} {title}
      </button>
      {open && (
        <div style={{
          fontSize: '0.9rem', lineHeight: 1.4,
          padding: '0.4rem 0.6rem',
        }}>
          {children}
        </div>
      )}
    </div>
  );
}
